import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { HeadingNode, type HeadingTagType, $createHeadingNode } from "@lexical/rich-text"
import { ListNode, ListItemNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list"
import { LinkNode } from "@lexical/link"
import { $patchStyleText } from "@lexical/selection"
import {
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    $getSelection,
    $isRangeSelection,
    $getRoot,
    $isParagraphNode,
    $createParagraphNode,
    type EditorState,
    type LexicalEditor,
    COMMAND_PRIORITY_CRITICAL,
    SELECTION_CHANGE_COMMAND,
} from "lexical"
import { $setBlocksType } from "@lexical/selection"
import { $isHeadingNode } from "@lexical/rich-text"
import { cn } from "@/lib/utils"
import { VariableNode, VariableTransformPlugin, insertVariable } from "./VariableNode"

// ─── Props ────────────────────────────────────────────────────────────────────

export interface EmailTemplateEditorProps {
    value: string
    onChange: (html: string) => void
    availableVariables: string[]
    placeholder?: string
    simpleMode?: boolean
}

// ─── Load initial HTML plugin ─────────────────────────────────────────────────

function LoadInitialHtmlPlugin({ html }: { html: string }): null {
    const [editor] = useLexicalComposerContext()
    const loadedRef = useRef(false)

    useEffect(() => {
        if (loadedRef.current) return
        if (!html) return
        loadedRef.current = true

        editor.update(() => {
            const parser = new DOMParser()
            const dom = parser.parseFromString(html, "text/html")
            const nodes = $generateNodesFromDOM(editor, dom)
            const root = $getRoot()
            root.clear()
            if (nodes.length > 0) {
                root.append(...nodes)
            }
        })
    }, [editor, html])

    return null
}

// ─── HTML export plugin ───────────────────────────────────────────────────────

function HtmlExportPlugin({ onChange }: { onChange: (html: string) => void }): React.ReactElement {
    const [editor] = useLexicalComposerContext()

    const handleChange = useCallback(
        (editorState: EditorState) => {
            editorState.read(() => {
                const html = $generateHtmlFromNodes(editor, null)
                onChange(html)
            })
        },
        [editor, onChange],
    )

    return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
}

// ─── Toolbar plugin ───────────────────────────────────────────────────────────

type BlockType = "paragraph" | "h1" | "h2" | "h3"

interface ToolbarState {
    isBold: boolean
    isItalic: boolean
    isUnderline: boolean
    blockType: BlockType
    elementFormat: string
}

function ToolbarPlugin({ availableVariables, simpleMode }: { availableVariables: string[]; simpleMode?: boolean }): React.ReactElement {
    const { t } = useTranslation()
    const [editor] = useLexicalComposerContext()
    const [state, setState] = useState<ToolbarState>({
        isBold: false,
        isItalic: false,
        isUnderline: false,
        blockType: "paragraph",
        elementFormat: "left",
    })

    const updateToolbar = useCallback(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return

        setState((prev) => ({
            ...prev,
            isBold: selection.hasFormat("bold"),
            isItalic: selection.hasFormat("italic"),
            isUnderline: selection.hasFormat("underline"),
        }))

        const anchorNode = selection.anchor.getNode()
        const element =
            anchorNode.getKey() === "root"
                ? anchorNode
                : anchorNode.getTopLevelElementOrThrow()

        if ($isHeadingNode(element)) {
            setState((prev) => ({ ...prev, blockType: element.getTag() as BlockType }))
        } else if ($isParagraphNode(element)) {
            setState((prev) => ({ ...prev, blockType: "paragraph" }))
        }
    }, [])

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => {
                updateToolbar()
                return false
            },
            COMMAND_PRIORITY_CRITICAL,
        )
    }, [editor, updateToolbar])

    const formatBlock = (tag: BlockType) => {
        editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return
            if (tag === "paragraph") {
                $setBlocksType(selection, () => $createParagraphNode())
            } else {
                $setBlocksType(selection, () => $createHeadingNode(tag as HeadingTagType))
            }
        })
    }

    const applyColor = (color: string) => {
        editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
                $patchStyleText(selection, { color })
            }
        })
    }

    const applyFontSize = (size: string) => {
        editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
                $patchStyleText(selection, { "font-size": size })
            }
        })
    }

    const btnClass = (active?: boolean) =>
        cn(
            "px-2.5 py-1.5 text-xs font-black rounded-xl transition-colors border",
            active
                ? "bg-charcoal text-white border-charcoal"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100",
        )

    const sepClass = "w-px h-6 bg-slate-200 mx-1"

    return (
        <div className="space-y-2">
            {/* Toolbar row */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-2xl border-b-0">
                {/* Text format */}
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.bold")}
                    className={btnClass(state.isBold)}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
                >
                    <strong>B</strong>
                </button>
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.italic")}
                    className={btnClass(state.isItalic)}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
                >
                    <em>I</em>
                </button>
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.underline")}
                    className={btnClass(state.isUnderline)}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
                >
                    <span style={{ textDecoration: "underline" }}>U</span>
                </button>

                <div className={sepClass} />

                {/* Alignment */}
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.alignLeft")}
                    className={btnClass(state.elementFormat === "left")}
                    onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")}
                >
                    ≡
                </button>
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.alignCenter")}
                    className={btnClass(state.elementFormat === "center")}
                    onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")}
                >
                    ☰
                </button>
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.alignRight")}
                    className={btnClass(state.elementFormat === "right")}
                    onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")}
                >
                    ≡
                </button>

                <div className={sepClass} />

                {/* Heading */}
                <select
                    value={state.blockType}
                    onChange={(e) => formatBlock(e.target.value as BlockType)}
                    className="text-xs font-bold bg-white border border-slate-200 rounded-xl px-2 py-1.5 text-slate-600 hover:bg-slate-100 cursor-pointer"
                    title={t("admin.settings.emailEditor.heading")}
                >
                    <option value="paragraph">{t("admin.settings.emailEditor.headingNormal")}</option>
                    <option value="h1">H1</option>
                    <option value="h2">H2</option>
                    <option value="h3">H3</option>
                </select>

                <div className={sepClass} />

                {/* Font color */}
                <label
                    className="flex items-center gap-1 px-2 py-1.5 bg-white border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100"
                    title={t("admin.settings.emailEditor.fontColor")}
                >
                    <span className="text-xs font-black text-slate-600">A</span>
                    <input
                        type="color"
                        className="w-4 h-4 rounded cursor-pointer border-0 p-0"
                        defaultValue="#000000"
                        onChange={(e) => applyColor(e.target.value)}
                    />
                </label>

                {/* Font size */}
                <select
                    onChange={(e) => applyFontSize(e.target.value)}
                    defaultValue=""
                    className="text-xs font-bold bg-white border border-slate-200 rounded-xl px-2 py-1.5 text-slate-600 hover:bg-slate-100 cursor-pointer"
                    title={t("admin.settings.emailEditor.fontSize")}
                >
                    <option value="" disabled>{t("admin.settings.emailEditor.fontSize")}</option>
                    <option value="12px">12px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="24px">24px</option>
                </select>

                <div className={sepClass} />

                {/* Lists */}
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.listUnordered")}
                    className={btnClass()}
                    onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
                >
                    •≡
                </button>
                <button
                    type="button"
                    title={t("admin.settings.emailEditor.listOrdered")}
                    className={btnClass()}
                    onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
                >
                    1≡
                </button>
            </div>

            {/* Variable chips */}
            {!simpleMode && availableVariables.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200 border-t-0 border-b-0">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400 mr-1">
                        {t("admin.settings.emailEditor.availableVariables")}:
                    </span>
                    {availableVariables.map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => insertVariable(editor, v)}
                            className="cursor-pointer bg-slate-100 hover:bg-brand/20 text-slate-700 text-xs font-mono px-2 py-1 rounded-lg border border-slate-200 transition-colors"
                        >
                            {`{{${v}}}`}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── Main editor component ────────────────────────────────────────────────────

export function EmailTemplateEditor({
    value,
    onChange,
    availableVariables,
    placeholder,
    simpleMode,
}: EmailTemplateEditorProps): React.ReactElement {
    const { t } = useTranslation()

    const initialConfig = {
        namespace: "EmailTemplateEditor",
        theme: {},
        nodes: [VariableNode, HeadingNode, ListNode, ListItemNode, LinkNode],
        onError: (err: unknown) => console.error("[EmailTemplateEditor]", err),
    }

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="border border-slate-200 rounded-2xl overflow-hidden">
                <ToolbarPlugin availableVariables={availableVariables} simpleMode={simpleMode} />
                <div className="relative bg-white min-h-[200px]">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="outline-none p-4 min-h-[200px] text-sm text-slate-800 font-normal prose prose-sm max-w-none focus:ring-0"
                                style={{ minHeight: "200px" }}
                            />
                        }
                        placeholder={
                            <div className="absolute top-4 left-4 text-slate-400 text-sm pointer-events-none select-none">
                                {placeholder ?? t("admin.settings.emailEditor.placeholder")}
                            </div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </div>
            </div>

            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            <VariableTransformPlugin />
            <LoadInitialHtmlPlugin html={value} />
            <HtmlExportPlugin onChange={onChange} />
        </LexicalComposer>
    )
}
